import { apiClient } from './client';
import type {
  BulkDeleteResponseBody,
  EntityType,
  LinkedCounts,
  RestoreResponseBody,
} from '../types';

export interface SoftDeleteOptions {
  reason?: string;
  force?: boolean;
}

export interface LinkedItemsError {
  code: 'linked_items_exist';
  message: string;
  linked: LinkedCounts;
}

/** Type guard: was the 409 thrown because of linked children? */
export function isLinkedItemsError(err: unknown): LinkedItemsError | null {
  // axios error with FastAPI detail-object payload
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (
    detail &&
    typeof detail === 'object' &&
    (detail as { code?: string }).code === 'linked_items_exist'
  ) {
    return detail as LinkedItemsError;
  }
  return null;
}

/** Per-entity base paths used by the soft-delete endpoints. */
export const ENTITY_BASE_PATH: Record<EntityType, string> = {
  inserat: '/listings',
  adresse: '/adressen',
  haus: '/haeuser',
  einheit: '/einheiten',
  vermieter: '/vermieter',
  verfahren: '/verfahren',
  anfrage: '/anfragen',
  bebauungsplan: '/bebauungsplaene',
};

export const ENTITY_LABEL: Record<EntityType, { singular: string; plural: string }> = {
  inserat: { singular: 'Inserat', plural: 'Inserate' },
  adresse: { singular: 'Adresse', plural: 'Adressen' },
  haus: { singular: 'Haus', plural: 'Häuser' },
  einheit: { singular: 'Objekt', plural: 'Objekte' },
  vermieter: { singular: 'Vermieter', plural: 'Vermieter' },
  verfahren: { singular: 'Verfahren', plural: 'Verfahren' },
  anfrage: { singular: 'Anfrage', plural: 'Anfragen' },
  bebauungsplan: { singular: 'Bebauungsplan', plural: 'Bebauungspläne' },
};

export async function softDeleteOne(
  entity: EntityType,
  id: number,
  { reason, force }: SoftDeleteOptions = {},
): Promise<void> {
  const base = ENTITY_BASE_PATH[entity];
  const params: Record<string, unknown> = {};
  if (force) params.force = true;
  await apiClient.delete(`${base}/${id}`, {
    params,
    data: reason ? { reason } : undefined,
  });
}

export async function bulkSoftDelete(
  entity: EntityType,
  ids: number[],
  { reason, force }: SoftDeleteOptions = {},
): Promise<BulkDeleteResponseBody> {
  const base = ENTITY_BASE_PATH[entity];
  const { data } = await apiClient.post<BulkDeleteResponseBody>(`${base}/bulk-delete`, {
    ids,
    reason: reason || null,
    force: !!force,
  });
  return data;
}

export async function restoreOne(entity: EntityType, id: number): Promise<RestoreResponseBody> {
  const base = ENTITY_BASE_PATH[entity];
  const { data } = await apiClient.post<RestoreResponseBody>(`${base}/${id}/restore`);
  return data;
}

export async function bulkRestore(entity: EntityType, ids: number[]): Promise<RestoreResponseBody> {
  const base = ENTITY_BASE_PATH[entity];
  const { data } = await apiClient.post<RestoreResponseBody>(`${base}/bulk-restore`, { ids });
  return data;
}
